import React, { useState, useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import { authFetch, getWsUrl } from '../services/api';
import { buildTemplateEditPrompt, isTemplateIncrementalMode } from '../utils/templatePrompt';
import { ChatBubbleSkeleton } from './Skeleton';
import ReferenceDesignModal from './ReferenceDesignModal';
import HeroVideoModal from './HeroVideoModal';
import { HelpButton } from './Onboarding';
function statusIcon(text) {
  const t = (text || '').toLowerCase();
  if (t.includes('installing') || t.includes('dependencies')) return '📦';
  if (t.includes('building') || t.includes('preview')) return '🏗️';
  if (t.includes('completed') || t.includes('done') || t.includes('ready')) return '✅';
  if (t.includes('error') || t.includes('failed')) return '❌';
  return '🤖';
}

// Splits a raw `file_progress` string into a phase tag (bracketed backend
// markers like "[scaffold]", "[retry]", "[pages]", "[backend]") or a real
// file path (folder + basename) — same shape as ProjectWorkspace.js's
// formatStep(), duplicated here since this is the only place the file
// list renders now.
function formatProgressFile(file) {
  const tagMatch = /^\[(\w+)\]\s*(.*)$/.exec(file || '');
  if (tagMatch) return { tag: tagMatch[1], folder: '', text: tagMatch[2] || '' };
  const parts = (file || '').split('/');
  const base = parts.pop();
  return { tag: null, folder: parts.length ? parts.join('/') + '/' : '', text: base };
}

const INTENT_ICON = {
  PROJECT_QUESTION: '🧠',
  GENERAL_CHAT: '💬',
  NEW_PROJECT: '⚡',
  PROJECT_MODIFICATION: '🔧',
};

// AGENT-THINKING-TRANSPARENCY-FIX: the backend already fires a `publish()`
// event for every real quality/repair pass it runs during generation
// (core/auto_fix_loop.py, core/orchestrator.py) — wiring up auth, enforcing
// role permissions, filling in a requirement the prompt asked for but the
// first pass missed, etc. None of that ever reached the user before: it
// only showed up as a line in backend logs. Mapping each one to a plain-
// language line here surfaces the SAME real work the agent is doing,
// instead of a generic spinner — this is real activity, not decorative
// narration, so it only ever shows an event the backend actually sent.
const QUALITY_EVENT_LABELS = {
  // Pipeline / stage markers
  chunked_path_started: 'Breaking your request into parallel build steps',
  single_shot_started: 'Starting generation',
  prompt_analyzed: 'Analyzed your request',
  dynamic_generation_started: 'Setting up backend & database',
  dynamic_backend_started: 'Building the backend API',
  dynamic_backend_completed: 'Backend API ready',
  frontend_wiring_started: 'Connecting frontend to the backend',
  frontend_wiring_completed: 'Frontend connected to the backend',
  dynamic_generation_completed: 'Backend & database ready',
  design_system_generated: 'Choosing a design system for your app',
  // Self-healing / verification — real build + syntax checks, not decorative
  syntax_retry_started: 'Fixing a syntax issue',
  syntax_retry_completed: 'Syntax issue fixed',
  syntax_fix_started: 'Fixing a syntax issue',
  syntax_fix_completed: 'Syntax issue fixed',
  build_verify_started: 'Running a real build to verify everything compiles',
  build_verify_passed: 'Build verified',
  build_fix_started: 'Fixing a build issue',
  build_fix_completed: 'Build issue fixed',
  build_degraded: 'Simplified a page that couldn’t be auto-fixed',
  validation_completed: 'Validated the generated code',
  auto_fix_started: 'Fixing an issue found during testing',
  auto_fix_completed: 'Issue fixed',
  completeness_fix_started: 'Checking for anything your prompt asked for that’s still missing',
  completeness_fix_completed: 'Filled in missing requirements',
  behavioral_fix_started: 'Wiring up interactive features',
  behavioral_fix_completed: 'Interactive features wired up',
  cart_wiring_fix_completed: 'Connected "Add to Cart" to a real cart',
  auth_wiring_fix_completed: 'Wired up login & signup',
  route_protection_fix_completed: 'Protected private pages behind login',
  rbac_enforcement_fix_completed: 'Enforced role-based permissions',
  interactive_wiring_fix_completed: 'Wired up buttons & forms',
  placeholder_fix_completed: 'Replaced placeholder content with real content',
  checkout_depth_fix_completed: 'Completed the checkout flow',
  generic_placeholder_fix_completed: 'Renamed generic placeholder names',
  ecommerce_richness_fix_completed: 'Added richer product details',
  broken_hero_positioning_fix_completed: 'Fixed hero section layout',
  nav_duplicates_removed: 'Cleaned up duplicate navigation links',
  tailwind_color_fix_completed: 'Fixed invalid color classes',
};

const ProjectChat = forwardRef(function ProjectChat({ projectId, onFilesUpdated, onGeneratingChange, onOpenImport, onRegisterImportSuccess, initialJobId, hasFiles, templateMeta, hasProjectFiles, isViewer = false }, ref) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [generating, setGenerating] = useState(!!initialJobId);
  const [intentLoading, setIntentLoading] = useState(false);
  const pollIntervalRef = useRef(null);
  const [currentJobId, setCurrentJobId] = useState(initialJobId || null);
  const streamSinceRef = useRef('0'); // stream position at job creation — avoids replaying old messages
  const [pipeline, setPipeline] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [showReferenceModal, setShowReferenceModal] = useState(false);
  // STRICT-SCOPE: "Match my prompt only" toggle — opt-in, off by default,
  // remembered across sessions. When on, the backend builds only the pages the
  // prompt names (no extra Account/Register/Orders-style pages or their
  // repair passes), which cuts tokens and time. Design quality is unchanged.
  const [strictScope, setStrictScope] = useState(() => {
    try { return localStorage.getItem('strict_scope') === '1'; } catch { return false; }
  });
  const toggleStrictScope = () => setStrictScope(v => {
    const next = !v;
    try { localStorage.setItem('strict_scope', next ? '1' : '0'); } catch {}
    return next;
  });
  // VIDEO-HERO-FEATURE: message-scoped attachment — cleared after the
  // message it's attached to is sent, same lifecycle as a design
  // reference (see Job.hero_video_url's comment in db/models.py).
  const [showHeroVideoModal, setShowHeroVideoModal] = useState(false);
  const [heroVideoAttachment, setHeroVideoAttachment] = useState(null); // null | { videoUrl, posterUrl, heroStyle }
  const isTemplateBased = !!(templateMeta?.type);
  const templateFilesReady = !isTemplateBased || !!hasProjectFiles;
  const useIncrementalMode = isTemplateIncrementalMode(templateMeta, hasProjectFiles);
  const bottomRef = useRef(null);
  const wsRef = useRef(null);

  // Direct WebSocket — open as soon as we have a jobId, no hook abstraction
  useEffect(() => {
    if (!currentJobId) return;
    if (wsRef.current) { wsRef.current.close(); wsRef.current = null; }

    const url = getWsUrl(currentJobId, streamSinceRef.current);
    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onmessage = (e) => {
      let data;
      try { data = JSON.parse(e.data); } catch { return; }
      const evt = data.event || data.type || '';
      // Every status line goes through here, so a branch cannot add one
      // that repeats — which is exactly what 'generation_started' did.
      const pushStatus = (text) => {
        if (!text) return;
        setPipeline(prev =>
          prev.some(s => s.kind === 'status' && s.text === text) ? prev : [...prev, { kind: 'status', text }]
        );
      };
      if (evt === 'status') {
        pushStatus(data.message || data.detail || '');
      } else if (evt === 'file_started') {
        const path = data.path || data.file || '';
        if (path) setPipeline(prev =>
          prev.some(s => s.kind === 'file_started' && s.path === path) ? prev : [...prev, { kind: 'file_started', path }]
        );
      } else if (evt === 'file_completed') {
        const path = data.path || data.file || '';
        if (path) setPipeline(prev =>
          prev.some(s => s.kind === 'file_completed' && s.path === path) ? prev : [...prev, { kind: 'file_completed', path }]
        );
      } else if (evt === 'generation_started') {
        // TWICE, AND WRONG THE SECOND TIME. This said "Understanding
        // prompt", which the backend has already published as
        // "Understanding Prompt" at the very start — different casing, so
        // the de-duplication the 'status' branch does could not catch it,
        // and it was appended unconditionally here besides. A user saw
        // the step repeat after planning, the design system, the data
        // model, the endpoints and the page plan had all gone by.
        //
        // single_shot_generator.py publishes this event immediately
        // before the code-writing call, so by then the prompt is long
        // understood — the honest label is what is about to happen.
        pushStatus('Writing the code');
      } else if (evt === 'generation_completed') {
        pushStatus('Building preview');
      } else if (evt === 'preview_checkpoint') {
        // PREVIEW-CHECKPOINT: the backend just saved the first complete,
        // validated version of the app (well before its slower repair passes
        // finish). Refetch project files so the Preview tab shows it now
        // instead of waiting for the whole job; the normal completion path
        // below still refreshes again with the final, fully repaired files.
        pushStatus('Preview ready — refining in background');
        if (onFilesUpdated) onFilesUpdated();
      } else if (evt === 'file_progress') {
        // FILE-LIST-TO-CHAT-FIX: the "replit style" generation path
        // (page_generator.py) only ever emits `file_progress` — never
        // `file_started`/`file_completed`/`status` above — so on that path
        // this pipeline stayed empty and the only place the scaffold/file
        // list showed up was the center preview panel's GeneratingProgress.
        // Mirroring it here (and removing it from the center) puts file
        // names where the user actually wants them: in the chat.
        const raw = data.file || data.path || '';
        const status = data.status || 'running';
        if (raw) setPipeline(prev => {
          const idx = prev.findIndex(s => s.kind === 'file_progress' && s.file === raw);
          if (idx !== -1) {
            if (prev[idx].status === status) return prev;
            const next = [...prev];
            next[idx] = { ...next[idx], status };
            return next;
          }
          return [...prev, { kind: 'file_progress', file: raw, status }];
        });
      } else if (evt === 'budget_checkpoint') {
        // A heads-up, not a question: the build keeps going.
        const text = data.message || 'This build is larger than most.';
        setPipeline(prev =>
          prev.some(s => s.kind === 'budget_note' && s.text === text) ? prev : [...prev, { kind: 'budget_note', text }]
        );
      } else if (evt === 'budget_approval_requested') {
        // The one event that needs an answer. No answer means the build
        // continues, so this is a choice, never a blocker.
        setPipeline(prev =>
          // Only an UNANSWERED ask blocks a new one. Keying the dedupe on
          // the kind alone meant the first question was the only one this
          // session ever showed: once answered it stayed in the pipeline,
          // so every later checkpoint was silently swallowed and the user
          // could not keep approving a build they had already said yes to.
          prev.some(s => s.kind === 'budget_ask' && !s.answered) ? prev : [...prev, {
            kind: 'budget_ask',
            // Stable identity, so a failed answer can put back exactly
            // this question rather than guessing at "the open one".
            askedAt: Date.now(),
            text: data.message || 'This app needs more work than a usual build. Continue?',
            waitSeconds: data.wait_seconds || 180,
            answered: null,
          }]
        );
      } else if (evt === 'budget_approval_granted' || evt === 'budget_approval_declined'
                 || evt === 'budget_approval_timeout' || evt === 'budget_ceiling_reached') {
        const answered = evt === 'budget_approval_declined' ? 'stopped' : 'continued';
        setPipeline(prev => {
          // Only the question still open, so an earlier 'stopped' is not
          // rewritten to 'continued' by a later checkpoint's outcome.
          const next = prev.map(s => (s.kind === 'budget_ask' && !s.answered) ? { ...s, answered } : s);
          const text = data.message || '';
          return text ? [...next, { kind: 'budget_note', text }] : next;
        });
      } else if (QUALITY_EVENT_LABELS[evt]) {
        pushStatus(QUALITY_EVENT_LABELS[evt]);
      }
    };

    ws.onerror = () => {};
    ws.onclose = () => {};

    return () => { ws.close(); wsRef.current = null; };
  }, [currentJobId]);

  useEffect(() => {
    if (projectId) fetchHistory();
  }, [projectId]);

  useEffect(() => {
    if (initialJobId) {
      // RESUME-IN-PROGRESS-JOB-FIX: this used to only start status polling
      // — currentJobId (this component's OWN state, keyed by the WebSocket-
      // opening effect below) was left at whatever useState(initialJobId ||
      // null) captured on the very first render. That's fine when
      // initialJobId is already known at mount, but ProjectWorkspace.js now
      // discovers an in-progress job ASYNCHRONOUSLY (after a page refresh,
      // there's no route state that survives it) and updates this prop
      // later — without also setting currentJobId here, live log streaming
      // never resumed even though status polling correctly did.
      setCurrentJobId(initialJobId);
      setGenerating(true);
      if (onGeneratingChange) onGeneratingChange(true, initialJobId);
      pollJob(initialJobId);
    }
  }, [initialJobId]);

  useEffect(() => {
    if (!onRegisterImportSuccess) return;
    onRegisterImportSuccess((data) => {
      const count = data?.fileCount ?? '?';
      const added = data?.changes?.added?.length ?? 0;
      const modified = data?.changes?.modified?.length ?? 0;
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `✅ Imported ${count} files into this project (${added} added, ${modified} updated). Preview and Code tabs have been refreshed.`,
        created_at: new Date().toISOString(),
      }]);
    });
  }, [onRegisterImportSuccess]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, pipeline]);

  // The build is waiting on this answer for a short time; no answer means
  // it continues. Only an explicit "stop" ends it.
  // THE STOP BUTTON HAS TO REACH THE SERVER. It used to clear the poll
  // interval, flip `generating` to false and print "Generation stopped."
  // — and tell the backend nothing at all. The orchestrator kept
  // running, kept spending tokens, and finished by writing its project
  // over the one the user believed they had stopped. Reported by a user
  // who had to have the job killed by hand.
  //
  // PATCH status=cancelled now revokes the Celery task as well as
  // writing the row (see api/jobs.py:revoke_running_task_for_job), so
  // this is the whole cancel.
  const cancelGeneration = async () => {
    const jobId = currentJobId;
    if (pollIntervalRef.current) { clearInterval(pollIntervalRef.current); pollIntervalRef.current = null; }
    setGenerating(false);
    if (onGeneratingChange) onGeneratingChange(false);
    if (!jobId) {
      setMessages(prev => [...prev, { role: 'assistant', content: '⏹️ Generation stopped.', created_at: new Date().toISOString() }]);
      return;
    }
    // Say it is stopping, not that it stopped, until the server confirms.
    setMessages(prev => [...prev, { role: 'assistant', content: '⏹️ Stopping generation…', created_at: new Date().toISOString() }]);
    try {
      const res = await authFetch(`/api/jobs/${jobId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'cancelled' }),
      });
      if (!res || !res.ok) throw new Error(`HTTP ${res && res.status}`);
      setMessages(prev => [...prev, { role: 'assistant', content: '⏹️ Generation cancelled. Everything built so far is kept.', created_at: new Date().toISOString() }]);
    } catch (e) {
      console.error('Failed to cancel job', e);
      // Never claim it stopped when the request failed — the build is
      // still running and still spending.
      setMessages(prev => [...prev, { role: 'assistant', content: '⚠️ Could not reach the server to cancel. The build may still be running — reload and try Stop again.', created_at: new Date().toISOString() }]);
    }
  };

  const respondToBudget = async (approved) => {
    const jobId = currentJobId;
    // The question this click is answering, identified so a failure can
    // put back exactly that one. Marking "the unanswered ask" cannot undo
    // itself: after the optimistic mark there is no unanswered ask left.
    const open = [...pipeline].reverse().find(s => s.kind === 'budget_ask' && !s.answered);
    const askedAt = open ? open.askedAt : null;
    const mark = (answered) => setPipeline(prev => prev.map(s =>
      (s.kind === 'budget_ask' && s.askedAt === askedAt) ? { ...s, answered } : s
    ));
    mark(approved ? 'continued' : 'stopped');
    if (!jobId) return;
    try {
      const res = await authFetch(`/api/jobs/${jobId}/budget-decision?approved=${approved}`, { method: 'POST' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
    } catch (e) {
      // NEVER SHOW AN ANSWER THE BUILD NEVER GOT. This marked the
      // question answered and then only console.error'd on failure, so a
      // user who clicked Stop on a 400-credit build read "stopped" while
      // the build kept spending and then timed out into continuing.
      // Put the question back so it can be answered again.
      console.error('Failed to send budget decision', e);
      mark(null);
      setPipeline(prev => [...prev, {
        kind: 'budget_note',
        text: 'Could not reach the server with that answer — the build has not been told. Please answer again.',
      }]);
    }
  };

  const fetchHistory = async () => {
    const startedAt = Date.now();
    try {
      const res = await authFetch(`/api/projects/${projectId}/history`);
      if (res.ok) {
        const server = await res.json();
        // DO NOT CLOBBER WHAT WAS SAID WHILE THIS REQUEST WAS IN FLIGHT.
        //
        // This was a blind `setMessages(server)`, and it runs on mount —
        // the exact moment ProjectWorkspace hands the create-screen's
        // prompt to sendFromHero. That optimistically appends the user's
        // message and THEN posts it, so the order was: history request
        // leaves -> prompt appended locally -> history response arrives
        // (without the prompt, because the server had not written it
        // yet) -> replace -> the prompt vanishes. The pipeline kept
        // streaming, so the chat showed a build running for a message
        // nobody could see, and only a refresh brought it back.
        //
        // Merge instead: the server's record wins for anything it knows
        // about, and whatever is only local is kept on the end rather
        // than thrown away.
        setMessages(prev => {
          const key = (m) => `${m.role}\u0000${m.content}`;
          const known = new Set(server.map(key));
          return [...server, ...prev.filter(m => !known.has(key(m)))];
        });
      }
    } catch (e) {
      console.error('Failed to fetch history', e);
    } finally {
      const elapsed = Date.now() - startedAt;
      setTimeout(() => setHistoryLoading(false), Math.max(0, 350 - elapsed));
    }
  };

  // SHORT-FOLLOWUP-FIX: confirmed live — "regenerate it" got rejected by
  // this check (only "regenerate" is longer than 2 chars; "it" is
  // filtered out, leaving 1 "real" word against a >=2 requirement), so a
  // completely legitimate one-line follow-up after a failed generation
  // ("regenerate it", "fix it", "try again") got the generic "describe
  // what you want" canned message instead of ever reaching the backend.
  const _SHORT_FOLLOWUP_RE = /^(regenerate( it)?|retry|try again|redo|fix( it| this)?|continue|proceed|again|do it|yes|please)[.!]?$/i;

  const validatePrompt = (text) => {
    const trimmed = text.trim();
    if (_SHORT_FOLLOWUP_RE.test(trimmed)) {
      return { valid: true };
    }
    const words = text.split(/\s+/).filter(w => w.length > 2);
    if (words.length < 2) {
      return { valid: false, message: "⚠️ Please describe what you want in more detail.\n\n• \"Build a restaurant landing page\"\n• \"Add a contact form\"\n• \"How do I run this project?\"" };
    }
    const vowelRatio = (text.match(/[aeiou]/gi) || []).length / text.length;
    const commonWords = ['build','create','make','add','change','update','remove','delete','page','app','website','how','what','where','which','explain','show'];
    const hasKnownWord = words.some(w => commonWords.includes(w.toLowerCase()));
    if (!hasKnownWord && vowelRatio < 0.25 && words.length < 10) {
      return { valid: false, message: "🤔 I couldn't understand that. Try:\n\n• \"Build a food delivery app\"\n• \"Add dark mode\"\n• \"How do I deploy this?\"" };
    }
    return { valid: true };
  };

  const handleSend = async () => {
    if (!input.trim() || generating || intentLoading || !templateFilesReady) return;
    const displayMsg = input.trim();

    const validation = validatePrompt(displayMsg);
    if (!validation.valid) {
      setMessages(prev => [...prev,
        { role: 'user', content: displayMsg, created_at: new Date().toISOString() },
        { role: 'assistant', content: validation.message, created_at: new Date().toISOString() },
      ]);
      setInput('');
      return;
    }

    const apiPrompt = isTemplateBased ? buildTemplateEditPrompt(displayMsg, templateMeta) : displayMsg;
    const attachedHeroVideo = heroVideoAttachment; // snapshot — this message only
    setMessages(prev => [...prev, {
      role: 'user',
      content: attachedHeroVideo ? `${displayMsg}\n\n🎬 ${attachedHeroVideo.heroStyle === 'none' ? 'No-hero-visual style' : 'Hero video'} attached` : displayMsg,
      created_at: new Date().toISOString(),
    }]);
    setInput('');
    setHeroVideoAttachment(null); // one-shot — cleared once it rides along with this message
    setIntentLoading(true);

    try {
      const res = await authFetch(`/api/projects/${projectId}/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: apiPrompt,
          ...(strictScope ? { strict_scope: true } : {}),
          ...(attachedHeroVideo ? { hero_video_url: attachedHeroVideo.videoUrl, hero_poster_url: attachedHeroVideo.posterUrl, hero_style: attachedHeroVideo.heroStyle } : {}),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Request failed');

      const intent = data.intent;

      if (intent === 'PROJECT_QUESTION' || intent === 'GENERAL_CHAT') {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: data.answer,
          sources: data.sources || [],
          intent,
          created_at: new Date().toISOString(),
        }]);
      } else {
        setPipeline([]);
        setGenerating(true);
        streamSinceRef.current = data.stream_since || '0';
        setCurrentJobId(data.job_id);
        if (onGeneratingChange) onGeneratingChange(true, data.job_id);
        if (data.job_id) pollJob(data.job_id);
      }
    } catch (e) {
      setMessages(prev => [...prev, { role: 'assistant', content: `❌ Error: ${e.message}`, created_at: new Date().toISOString() }]);
    } finally {
      setIntentLoading(false);
    }
  };

  const handleReferenceGenerationStarted = (jobId) => {
    setShowReferenceModal(false);
    setMessages(prev => [...prev, { role: 'user', content: '🎨 Build from design reference', created_at: new Date().toISOString() }]);
    setGenerating(true);
    setPipeline([]);
    setCurrentJobId(jobId);
    if (onGeneratingChange) onGeneratingChange(true, jobId);
    pollJob(jobId);
  };

  const pollJob = async (jobId) => {
    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    // TRANSIENT-POLL-FAILURE-FIX: confirmed live — a momentary backend
    // hiccup (api container restart, brief network drop, laptop sleep/
    // resume) makes ONE `authFetch` call throw. The old code treated that
    // single throw as fatal: clearInterval + setGenerating(false), with NO
    // message to the user, even though the job kept running server-side
    // (celery isn't affected by the api container bouncing) and finished
    // normally minutes later. The UI was then permanently stuck showing
    // stale "still generating"/whatever-was-last-rendered state forever,
    // since nothing ever re-polled or re-fetched files after that point.
    // Fix: tolerate a run of consecutive failures (transient) and only
    // give up — with a clear, honest message — after several in a row.
    let consecutiveFailures = 0;
    const MAX_CONSECUTIVE_FAILURES = 8; // ~24s of continuous failure at 3s/tick
    const interval = setInterval(async () => {
      try {
        const res = await authFetch(`/api/jobs/${jobId}`);
        // TOKEN-REFRESH-GAP-FIX: authFetch now silently refreshes an
        // expired access token once — this only hits if the REFRESH token
        // is also gone (genuinely stale session), which authFetch can't
        // recover from on its own. Without this check, job.status would
        // just be undefined forever and the poll would spin silently
        // instead of ever telling the user why nothing is happening.
        if (res.status === 401) {
          clearInterval(interval);
          setGenerating(false);
          if (onGeneratingChange) onGeneratingChange(false);
          setMessages(prev => [...prev, {
            role: 'assistant',
            content: '⚠️ Your session expired while this was generating. Please log in again — your project and this generation are saved, and you can check its status once you\'re back in.',
            created_at: new Date().toISOString(),
          }]);
          return;
        }
        const job = await res.json();
        consecutiveFailures = 0; // a successful response proves the backend is reachable again
        if (['done', 'needs_attention', 'failed', 'insufficient_credits', 'cancelled'].includes(job.status)) {
          clearInterval(interval);
          // GENERATING STAYS TRUE UNTIL THE FINISHED FILES ARE LOADED.
          // It used to be cleared right here, before save-files had even
          // been called — so the Preview tab stopped showing the loader
          // and mounted a dev server against whatever files the browser
          // still had, which during a fresh build is the half-written
          // intermediate state. Cleared in the `finally` below instead,
          // after save-files and onFilesUpdated, so the first thing the
          // preview ever boots is the final project.
          try {
          if (job.status === 'done' || job.status === 'needs_attention') {
            try {
              await authFetch(`/api/projects/${projectId}/save-files`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ job_id: jobId }),
              });
            } catch (e) {
              console.warn('save-files:', e.message);
            }
            // WEAK-MODEL-VISIBILITY-FIX: build_verifier.py's real build gate
            // fails OPEN (job still marked "done" even after every repair
            // attempt failed) — job.build_incomplete is how that shows up
            // here. Without this, a build the pipeline itself couldn't get
            // to compile looked identical to a real success.
            // GRACEFUL-DEGRADE-FIX: distinct from build_incomplete above —
            // build_degraded_files means the build DOES compile, but one
            // or more requested pages were swapped for a placeholder to
            // get there (build_verifier.py's last-resort isolation step).
            // Better outcome than build_incomplete, still worth telling
            // the user which page(s) to ask for again.
            let degradedFiles = [];
            try { degradedFiles = job.build_degraded_files ? JSON.parse(job.build_degraded_files) : []; } catch {}

            // DEGRADED PAGES ARE NOT AN "ELSE": a job can both end
            // needs_attention AND have had pages swapped for placeholders
            // to get the build to compile. This note used to sit in the
            // last branch of an if/else chain, so needs_attention — the
            // most common outcome for a big build — hid it completely.
            // Confirmed live on job 278a3098: SettingsRoleEditPage and
            // SettingsSupplierEditPage were both replaced with "This
            // section is being finalized" and the user was never told.
            const degradedNote = degradedFiles.length > 0
              ? ` Also, ${degradedFiles.length === 1 ? 'this page' : 'these pages'} couldn't be built and ${degradedFiles.length === 1 ? 'was' : 'were'} replaced with a placeholder: ${degradedFiles.map(p => p.split('/').pop().replace(/\.(jsx|tsx|js|ts)$/, '')).join(', ')}. Ask in chat to regenerate ${degradedFiles.length === 1 ? 'it' : 'them'}.`
              : '';

            if (job.status === 'needs_attention') {
              // NEEDS-ATTENTION-FIX: the functional smoke-test gate could
              // not resolve a real defect after its full retry budget —
              // the actual generated code is saved as-is (never a
              // placeholder), but something concrete still needs a human
              // look. needs_attention_detail carries exactly what failed.
              setMessages(prev => [...prev, {
                role: 'assistant',
                // THE FALLBACK USED TO SEND PEOPLE TO THE PREVIEW TAB,
                // WHICH SHOWS NONE OF THIS. needs_attention_detail is the
                // only place the reason exists, and this is the only
                // component that displays it — nothing in Preview, Code
                // or Logs ever mentions it. For most of this project's
                // life the detail never arrived at all (JobResponse did
                // not list the field, so the API dropped it), so this
                // fallback was what users actually read, every time, and
                // it pointed at an empty tab. Say what is true instead.
                content: `⚠️ Code generated and saved, but an automatic check found a real problem it couldn't fix on its own.${job.needs_attention_detail ? `\n\n${job.needs_attention_detail}` : ' No further detail was recorded for this job.'}${degradedNote}\n\nTry "fix this error" in chat, or switch to a stronger model in Settings and regenerate.`,
                created_at: new Date().toISOString(),
              }]);
            } else if (job.build_incomplete) {
              setMessages(prev => [...prev, {
                role: 'assistant',
                content: `⚠️ Code generated, but automatic build verification could not confirm it compiles cleanly — this happens more often with a lighter/cheaper AI model.${degradedNote} Check the Preview tab: if it looks broken, try "fix this error" in chat, or switch to a stronger model in Settings and regenerate.`,
                created_at: new Date().toISOString(),
              }]);
            } else if (degradedFiles.length > 0) {
              const names = degradedFiles.map(p => p.split('/').pop().replace(/\.(jsx|tsx|js|ts)$/, '')).join(', ');
              setMessages(prev => [...prev, {
                role: 'assistant',
                content: `✅ Code generated and builds cleanly — but ${degradedFiles.length === 1 ? 'this page' : 'these pages'} couldn't be generated correctly and ${degradedFiles.length === 1 ? 'was' : 'were'} replaced with a placeholder: ${names}. Ask in chat to regenerate ${degradedFiles.length === 1 ? 'it' : 'them'}, or switch to a stronger model in Settings.`,
                created_at: new Date().toISOString(),
              }]);
            } else {
              setMessages(prev => [...prev, {
                role: 'assistant',
                content: '✅ Code generated! Preview and Code tabs updated.',
                created_at: new Date().toISOString(),
              }]);
            }
            if (onFilesUpdated) onFilesUpdated();
          } else {
            let errorMsg = '❌ Generation failed. Try a simpler prompt.';
            try {
              const result = job.result ? JSON.parse(job.result) : {};
              if (result.error_type === 'quota_exceeded' || (result.error && result.error.toLowerCase().includes('quota'))) {
                errorMsg = "⚠️ API quota exceeded! Your AI API key has run out of credits. Please add billing to your OpenAI/Anthropic account and try again.";
              } else if (result.error_type === 'budget_exceeded') {
                errorMsg = '⚠️ Monthly budget limit reached.';
              } else if (result.error) {
                errorMsg = `❌ ${result.error}`;
              }
            } catch {}
            setMessages(prev => [...prev, { role: 'assistant', content: errorMsg, created_at: new Date().toISOString() }]);
          }
          } finally {
            // Always, on every branch and even if save-files threw: a
            // loader that never goes away is worse than a preview that
            // boots a moment early.
            setGenerating(false);
            if (onGeneratingChange) onGeneratingChange(false);
          }
        }
      } catch (e) {
        consecutiveFailures += 1;
        if (consecutiveFailures < MAX_CONSECUTIVE_FAILURES) {
          // Transient — a container restart or a brief network drop.
          // Keep polling silently; the job itself is unaffected (it runs
          // in celery_worker, a separate process from this fetch).
          return;
        }
        clearInterval(interval);
        setGenerating(false);
        if (onGeneratingChange) onGeneratingChange(false);
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: "⚠️ Lost connection while checking generation status. Your job may still be running on the server — refresh the page in a bit to pick it back up; it will resume automatically if it's still going, or show the finished result if it's done.",
          created_at: new Date().toISOString(),
        }]);
      }
    }, 3000);
    pollIntervalRef.current = interval;
  };

  useImperativeHandle(ref, () => ({
    handleReferenceStarted: (jobId) => {
      setShowReferenceModal(false);
      setMessages(prev => [...prev, { role: 'user', content: '🎨 Build from design reference', created_at: new Date().toISOString() }]);
      setGenerating(true);
      setPipeline([]);
      setCurrentJobId(jobId);
      if (onGeneratingChange) onGeneratingChange(true, jobId);
      pollJob(jobId);
    },
    isBusy: () => generating || intentLoading,
    isGenerating: () => generating,
    stopGeneration: () => { cancelGeneration(); },
    openReferenceModal: () => setShowReferenceModal(true),
    // Lets the hero screen's 🎯 toggle share this same state
    setStrictScope: (v) => { try { localStorage.setItem('strict_scope', v ? '1' : '0'); } catch {} setStrictScope(!!v); },
    sendFromHero: (text, heroVideo = null) => {
      if (!text || generating || intentLoading) return;
      const displayMsg = text.trim();
      const validation = validatePrompt(displayMsg);
      if (!validation.valid) {
        setMessages(prev => [...prev,
          { role: 'user', content: displayMsg, created_at: new Date().toISOString() },
          { role: 'assistant', content: validation.message, created_at: new Date().toISOString() },
        ]);
        return;
      }
      const apiPrompt = isTemplateBased ? buildTemplateEditPrompt(displayMsg, templateMeta) : displayMsg;
      setMessages(prev => [...prev, {
        role: 'user',
        content: heroVideo ? `${displayMsg}\n\n🎬 ${heroVideo.heroStyle === 'none' ? 'No-hero-visual style' : 'Hero video'} attached` : displayMsg,
        created_at: new Date().toISOString(),
      }]);
      setIntentLoading(true);
      authFetch(`/api/projects/${projectId}/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: apiPrompt,
          ...(strictScope ? { strict_scope: true } : {}),
          ...(heroVideo ? { hero_video_url: heroVideo.videoUrl, hero_poster_url: heroVideo.posterUrl, hero_style: heroVideo.heroStyle } : {}),
        }),
      }).then(res => res.json()).then(data => {
        if (data.detail) throw new Error(data.detail);
        const intent = data.intent;
        if (intent === 'PROJECT_QUESTION' || intent === 'GENERAL_CHAT') {
          setMessages(prev => [...prev, { role: 'assistant', content: data.answer, sources: data.sources || [], intent, created_at: new Date().toISOString() }]);
        } else {
          setPipeline([]);
          setGenerating(true);
          streamSinceRef.current = data.stream_since || '0';
          setCurrentJobId(data.job_id);
          if (onGeneratingChange) onGeneratingChange(true, data.job_id);
          if (data.job_id) pollJob(data.job_id);
        }
      }).catch(e => {
        setMessages(prev => [...prev, { role: 'assistant', content: `❌ Error: ${e.message}`, created_at: new Date().toISOString() }]);
      }).finally(() => setIntentLoading(false));
    },
  }));

  const busy = generating || intentLoading;
  const placeholder = intentLoading
    ? 'Thinking…'
    : isTemplateBased
      ? (templateFilesReady ? 'Customize this template or ask a question…' : 'Loading template…')
      : (messages.length === 0 ? 'Describe your app or ask a question…' : 'Ask for changes or ask a question…');

  return (
    <div style={s.container} data-tour="project-chat">
      <style>{`
        @keyframes fadeIn { from{opacity:0;transform:translateY(6px);} to{opacity:1;transform:translateY(0);} }
        @keyframes pc-blink { 0%,100%{opacity:1} 50%{opacity:0.25} }
        .pc-input:focus { border-color: rgba(99,102,241,0.55) !important; box-shadow: 0 0 0 3px rgba(99,102,241,0.1) !important; }
        .pc-chip:hover { background: rgba(99,102,241,0.14) !important; border-color: rgba(99,102,241,0.38) !important; color: #c7d2fe !important; }
        .pc-plus:hover:not(:disabled) { border-color: rgba(99,102,241,0.45) !important; background: rgba(99,102,241,0.08) !important; }
        .pc-send:hover:not(:disabled) { opacity: 0.88; transform: scale(0.96); box-shadow: 0 4px 16px rgba(99,102,241,0.45) !important; }
        .pc-messages::-webkit-scrollbar { width: 4px; }
        .pc-messages::-webkit-scrollbar-track { background: transparent; }
        .pc-messages::-webkit-scrollbar-thumb { background: rgba(99,102,241,0.2); border-radius: 4px; }
      `}</style>
      <div style={s.header}>
        <div style={s.headerIcon}>⚡</div>
        <h3 style={s.title}>Project Chat</h3>
        {intentLoading && <span style={s.intentBadge}>🔍 routing…</span>}
        <div style={{ marginLeft: 'auto', flexShrink: 0 }}>
          <HelpButton
            onStartTour={() => {}}
            onNavigateProjects={() => {}}
            onGetView={() => 'workspace'}
            defaultOpen="agent"
          />
        </div>
      </div>

      <div style={s.messages} className="pc-messages">
        {historyLoading ? (
          <div className="skeleton-fade-in" style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%' }}>
            <ChatBubbleSkeleton align="left" width="78%" />
            <ChatBubbleSkeleton align="right" width="66%" />
            <ChatBubbleSkeleton align="left" width="82%" />
            <ChatBubbleSkeleton align="right" width="52%" />
          </div>
        ) : messages.length === 0 && !generating && (
          <div style={s.empty}>
            {isTemplateBased ? (
              <div style={s.templateBanner}>
                <div style={s.templateBannerIcon}>🎨</div>
                <div style={s.templateBannerTitle}>{templateMeta?.name || 'Template'} Loaded</div>
                <div style={s.templateBannerDesc}>
                  {templateFilesReady
                    ? 'Starter code is ready. Ask for changes or ask questions about the project.'
                    : 'Loading template files…'}
                </div>
                <div style={s.suggestions}>
                  {(templateMeta?.type === 'saas-landing'
                    ? ['Change accent color to blue', 'Add a FAQ section', 'How do I run this?', 'Update hero headline']
                    : templateMeta?.type === 'ecommerce-store'
                    ? ['Change store accent color', 'Add product filters', 'How do I deploy this?', 'Add a promo banner']
                    : templateMeta?.type === 'task-management-dashboard'
                    ? ['Add a new Kanban column', 'Change accent to teal', 'Where is the task list?', 'Add sprint analytics']
                    : templateMeta?.type === 'restaurant-website'
                    ? ['Change menu categories', 'Update hero section', 'How do I add a new page?', 'Customize reservation form']
                    : templateMeta?.type === 'healthcare-dashboard'
                    ? ['Change accent to mint green', 'Add a vitals chart', 'Where is authentication?', 'Add a prescription tracker']
                    : templateMeta?.type === 'fitness-gym-website'
                    ? ['Add a class schedule section', 'Change accent to red', 'How do I run this?', 'Customize membership tiers']
                    : templateMeta?.type === 'real-estate-listing'
                    ? ['Add a mortgage calculator', 'Change accent to emerald', 'Where is the search filter?', 'Add saved listings']
                    : templateMeta?.type === 'blog-cms'
                    ? ['Add a new destination card', 'Shift accent to lavender', 'How do I add a post?', 'Customize the layout']
                    : templateMeta?.type === 'admin-dashboard'
                    ? ['Add a new analytics panel', 'Shift accent to olive', 'Where is the sidebar?', 'Add a pipeline stage']
                    : ['Change color theme to blue', 'Add a blog section', 'How do I run this?', 'Add dark mode']
                  ).map(hint => (
                    <button key={hint} style={s.chip} onClick={() => setInput(hint)}>{hint}</button>
                  ))}
                </div>
              </div>
            ) : (
              <div style={s.emptyState}>
                <div style={s.emptyIcon}>⚡</div>
                <div style={s.emptyTitle}>Project Chat</div>
                <div style={s.emptyDesc}>
                  Describe what to build, ask for changes, or ask questions about your project.
                </div>
                <div style={s.suggestions}>
                  {['Build a Todo App', 'Create a SaaS landing page', 'How do I run this?', 'Where is authentication?'].map(hint => (
                    <button key={hint} style={s.chip} onClick={() => setInput(hint)}>{hint}</button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {!historyLoading && messages.map((msg, i) => (
          <div key={i} style={{ ...s.msg, ...(msg.role === 'user' ? s.userMsg : s.assistantMsg) }}>
            <span style={s.msgRole}>
              {msg.role === 'user' ? '👤' : (INTENT_ICON[msg.intent] || '🤖')}
            </span>
            <div style={{ minWidth: 0 }}>
              <p style={s.msgContent}>{msg.content}</p>
              {msg.sources && msg.sources.length > 0 && (
                <div style={s.sources}>
                  {msg.sources.slice(0, 4).map((src, si) => (
                    <span key={si} style={s.sourceChip}>📄 {src.file.split('/').pop()} L{src.lines}</span>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}

        {intentLoading && (
          <div style={{ ...s.msg, ...s.assistantMsg }}>
            <span style={s.msgRole}>🔍</span>
            <p style={{ ...s.msgContent, color:'var(--text-muted)', fontStyle:'italic' }}>
              Routing your message
              <span style={{ display:'inline-block', animation:'pc-blink 1s step-end infinite', marginLeft:2 }}>…</span>
            </p>
          </div>
        )}

        {generating && pipeline.length === 0 && (
          <div style={{ ...s.msg, ...s.assistantMsg, gap:10 }}>
            <span style={{ width:6, height:6, borderRadius:'50%', background:'#6366f1', flexShrink:0, marginTop:6, display:'inline-block', animation:'pc-blink 1s ease-in-out infinite' }} />
            <p style={{ ...s.msgContent, color:'var(--text-muted)', fontStyle:'italic' }}>Generating project…</p>
          </div>
        )}

        {generating && pipeline.map((step, i) => {
          if (step.kind === 'budget_note') {
            return (
              <div key={i} style={{ ...s.msg, ...s.assistantMsg, padding:'7px 12px' }}>
                <span style={s.msgRole} aria-hidden>◷</span>
                <p style={{ ...s.msgContent, fontSize:12.5, color:'#fcd34d' }}>{step.text}</p>
              </div>
            );
          }
          if (step.kind === 'budget_ask') {
            const answered = step.answered;
            return (
              <div key={i} style={{ ...s.msg, ...s.assistantMsg, padding:'11px 13px',
                                    border:'1px solid rgba(252,211,77,0.35)', borderRadius:10 }}>
                <span style={s.msgRole} aria-hidden>◷</span>
                <div style={{ flex:1 }}>
                  <p style={{ ...s.msgContent, fontSize:12.5, marginBottom: answered ? 0 : 9 }}>
                    {step.text}
                  </p>
                  {answered ? (
                    <p style={{ ...s.msgContent, fontSize:12, color:'var(--text-muted)' }}>
                      {answered === 'stopped'
                        ? 'Stopped — everything built so far is kept.'
                        : 'Continuing the build.'}
                    </p>
                  ) : (
                    <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
                      <button type="button" onClick={() => respondToBudget(true)}
                              style={{ padding:'5px 13px', borderRadius:7, border:'none',
                                       background:'#6366f1', color:'#fff', fontSize:12,
                                       fontWeight:600, cursor:'pointer' }}>
                        Continue
                      </button>
                      <button type="button" onClick={() => respondToBudget(false)}
                              style={{ padding:'5px 13px', borderRadius:7, cursor:'pointer',
                                       border:'1px solid var(--border, rgba(255,255,255,0.18))',
                                       background:'transparent', color:'var(--text-muted)',
                                       fontSize:12 }}>
                        Stop and keep what's built
                      </button>
                      <span style={{ fontSize:11, color:'var(--text-muted)' }}>
                        No answer in {step.waitSeconds}s means it continues.
                      </span>
                    </div>
                  )}
                </div>
              </div>
            );
          }
          if (step.kind === 'file_progress') {
            const done = step.status !== 'running';
            const { tag, folder, text } = formatProgressFile(step.file);
            return (
              <div key={i} style={{ ...s.msg, ...s.assistantMsg, padding:'7px 12px' }}>
                <span style={s.msgRole} aria-hidden>{done ? '✓' : '◌'}</span>
                <p style={{ ...s.msgContent, fontSize:12, fontFamily: '"JetBrains Mono",monospace', color: done ? '#6ee7b7' : '#a5b4fc' }}>
                  {tag && <span style={{ color:'var(--text-muted)', fontFamily:'inherit', marginRight:6 }}>[{tag}]</span>}
                  {folder && <span style={{ color:'rgba(99,102,241,0.6)' }}>{folder}</span>}
                  {text}
                </p>
              </div>
            );
          }
          return (
            <div key={i} style={{ ...s.msg, ...s.assistantMsg, padding:'7px 12px' }}>
              <span style={s.msgRole} aria-hidden>
                {step.kind === 'file_completed' ? '✓' : step.kind === 'file_started' ? '◌' : '·'}
              </span>
              <p style={{ ...s.msgContent, fontSize:12, fontFamily: step.kind !== 'status' ? '"JetBrains Mono",monospace' : 'inherit', color: step.kind === 'file_completed' ? '#6ee7b7' : step.kind === 'file_started' ? '#a5b4fc' : 'var(--text-muted)' }}>
                {step.kind === 'status' && statusIcon(step.text) + ' ' + step.text}
                {step.kind === 'file_started' && step.path}
                {step.kind === 'file_completed' && step.path}
              </p>
            </div>
          );
        })}

        <div ref={bottomRef} />
      </div>

      {heroVideoAttachment && (
        <div style={s.heroVideoChip}>
          <span>🎬 {heroVideoAttachment.heroStyle === 'none' ? 'No hero visual' : 'Hero video'} attached — will be used in your next message</span>
          <button onClick={() => setHeroVideoAttachment(null)} style={s.heroVideoChipClose} title="Remove attachment">✕</button>
        </div>
      )}

      <div style={s.inputRow}>
        <span title={hasFiles ? 'Import disabled — project already has files' : 'Import repository'} style={{ display: 'inline-flex' }}>
          <button
            onClick={() => onOpenImport && onOpenImport()}
            style={{ ...s.plusBtn, ...(hasFiles || busy ? s.plusBtnDisabled : {}) }}
            disabled={hasFiles || busy}
          >+</button>
        </span>
        <span title={busy ? 'Please wait for the current generation to finish' : 'Build or update from a screenshot or reference URL'} style={{ display: 'inline-flex' }}>
          <button
            onClick={() => setShowReferenceModal(true)}
            style={{ ...s.plusBtn, ...(busy ? s.plusBtnDisabled : {}) }}
            disabled={busy}
          >🎨</button>
        </span>
        <span title={busy ? 'Please wait for the current generation to finish' : 'Add a hero video for your next message'} style={{ display: 'inline-flex' }}>
          <button
            onClick={() => setShowHeroVideoModal(true)}
            style={{ ...s.plusBtn, ...(busy ? s.plusBtnDisabled : {}), ...(heroVideoAttachment ? s.plusBtnActive : {}) }}
            disabled={busy}
          >🎬</button>
        </span>
        <span title={strictScope ? 'Match my prompt only: ON — builds only the pages you ask for (faster, fewer tokens). Click to turn off.' : 'Match my prompt only: OFF — builds a full production-ready site with extra pages. Click to build only what you ask for.'} style={{ display: 'inline-flex' }}>
          <button
            onClick={toggleStrictScope}
            style={{ ...s.plusBtn, ...(busy ? s.plusBtnDisabled : {}), ...(strictScope ? s.plusBtnActive : {}) }}
            disabled={busy}
            aria-pressed={strictScope}
          >🎯</button>
        </span>
        <textarea
          data-tour="prompt-input"
          className="pc-input"
          style={s.input}
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
          placeholder={placeholder}
          disabled={busy || !templateFilesReady}
          rows={1}
          onInput={e => { e.target.style.height = 'auto'; e.target.style.height = Math.min(e.target.scrollHeight, 200) + 'px'; }}
        />
        {generating ? (
          <button onClick={cancelGeneration}
                  style={{ ...s.sendBtn, background: '#ef4444' }} title="Stop and cancel this build">
            ■
          </button>
        ) : (
          <button onClick={handleSend} disabled={!input.trim() || intentLoading || !templateFilesReady} style={s.sendBtn}>
            →
          </button>
        )}
      </div>

      {showReferenceModal && (
        <ReferenceDesignModal
          projectId={projectId}
          onClose={() => setShowReferenceModal(false)}
          onStarted={handleReferenceGenerationStarted}
        />
      )}

      {showHeroVideoModal && (
        <HeroVideoModal
          onClose={() => setShowHeroVideoModal(false)}
          onAttached={({ videoUrl, posterUrl, heroStyle }) => setHeroVideoAttachment({ videoUrl, posterUrl, heroStyle })}
        />
      )}
    </div>
  );
});

export default ProjectChat;

const s = {
  container: {
    display:'flex', flexDirection:'column', height:'100%',
    background:'var(--bg-secondary)',
    borderLeft:'1px solid var(--border)',
    minWidth:0, overflow:'hidden',
  },

  // ── Header ──────────────────────────────────────────────────────────────
  header: {
    display:'flex', alignItems:'center', gap:10,
    padding:'12px 16px',
    borderBottom:'1px solid var(--border)',
    flexShrink:0,
    background:'var(--bg-card)',
    boxShadow:'0 1px 0 rgba(99,102,241,0.06)',
  },
  headerIcon: {
    width:28, height:28, borderRadius:8, flexShrink:0,
    background:'linear-gradient(135deg,#4f46e5,#7c3aed)',
    display:'flex', alignItems:'center', justifyContent:'center',
    fontSize:13, boxShadow:'0 2px 8px rgba(99,102,241,0.3)',
  },
  title: {
    fontSize:13, fontWeight:700, color:'var(--text-primary)',
    margin:0, letterSpacing:'0.01em',
  },
  intentBadge: {
    fontSize:10, color:'#a5b4fc',
    background:'rgba(99,102,241,0.1)',
    border:'1px solid rgba(99,102,241,0.2)',
    padding:'2px 8px', borderRadius:20, fontWeight:600,
  },

  // ── Message list ────────────────────────────────────────────────────────
  messages: {
    flex:1, overflowY:'auto', overflowX:'hidden',
    padding:'16px 14px',
    display:'flex', flexDirection:'column', gap:8,
    minWidth:0,
  },

  // ── Empty / idle states ─────────────────────────────────────────────────
  empty: { display:'flex', alignItems:'center', justifyContent:'center', height:'100%' },
  emptyState: {
    display:'flex', flexDirection:'column', alignItems:'center',
    padding:'28px 16px', textAlign:'center', gap:10,
  },
  emptyIcon: {
    width:44, height:44, borderRadius:14, marginBottom:2,
    background:'linear-gradient(135deg,rgba(99,102,241,0.12),rgba(139,92,246,0.08))',
    border:'1px solid rgba(99,102,241,0.18)',
    display:'flex', alignItems:'center', justifyContent:'center', fontSize:20,
  },
  emptyTitle: { fontSize:13, fontWeight:700, color:'var(--text-primary)', margin:0 },
  emptyDesc: {
    fontSize:12, color:'var(--text-muted)', lineHeight:1.65,
    maxWidth:240, textAlign:'center', margin:0,
  },

  // ── Bubbles ─────────────────────────────────────────────────────────────
  msg: {
    display:'flex', gap:8,
    padding:'9px 12px', borderRadius:12,
    maxWidth:'100%', minWidth:0, width:'fit-content',
    animation:'fadeIn 0.18s ease',
  },
  userMsg: {
    alignSelf:'flex-end',
    background:'linear-gradient(135deg,rgba(79,70,229,0.22),rgba(124,58,237,0.15))',
    border:'1px solid rgba(99,102,241,0.25)',
    maxWidth:'86%',
    boxShadow:'0 1px 6px rgba(99,102,241,0.12)',
  },
  assistantMsg: {
    alignSelf:'flex-start',
    background:'var(--bg-card)',
    border:'1px solid var(--border)',
    maxWidth:'95%',
    boxShadow:'0 1px 4px rgba(0,0,0,0.07)',
  },
  msgRole: { fontSize:13, flexShrink:0, marginTop:2, lineHeight:1 },
  msgContent: {
    fontSize:13, color:'var(--text-primary)', lineHeight:1.65,
    margin:0, wordBreak:'break-word', whiteSpace:'pre-wrap',
    overflowWrap:'anywhere', overflow:'hidden', minWidth:0,
  },

  // ── Sources ─────────────────────────────────────────────────────────────
  sources: { display:'flex', flexWrap:'wrap', gap:4, marginTop:6 },
  sourceChip: {
    fontSize:10, padding:'2px 7px', borderRadius:4,
    background:'rgba(99,102,241,0.1)', color:'#a5b4fc',
    border:'1px solid rgba(99,102,241,0.18)',
  },

  // ── Streaming pipeline rows ──────────────────────────────────────────────
  progressBox: {
    background:'var(--progress-bg)', borderRadius:10,
    padding:'12px 16px', margin:'4px 0',
    border:'1px solid var(--progress-border)', fontFamily:'monospace',
  },

  // ── Input composer ───────────────────────────────────────────────────────
  inputRow: {
    display:'flex', gap:6, padding:'10px 12px',
    borderTop:'1px solid var(--border)',
    alignItems:'flex-end', flexShrink:0,
    background:'var(--bg-card)',
    boxShadow:'0 -1px 0 rgba(99,102,241,0.05)',
  },
  plusBtn: {
    width:32, height:32, flexShrink:0,
    background:'var(--bg-input)',
    border:'1px solid var(--border)',
    borderRadius:8,
    color:'var(--text-secondary)',
    fontSize:16, fontWeight:400,
    cursor:'pointer',
    display:'flex', alignItems:'center', justifyContent:'center',
    lineHeight:1, padding:0,
    transition:'border-color 0.15s, background 0.15s, box-shadow 0.15s',
  },
  plusBtnDisabled: { opacity:0.3, cursor:'not-allowed' },
  plusBtnActive: { borderColor:'rgba(99,102,241,0.6)', background:'rgba(99,102,241,0.12)', color:'var(--accent-light)' },
  heroVideoChip: { display:'flex', alignItems:'center', justifyContent:'space-between', gap:8, background:'rgba(99,102,241,0.10)', border:'1px solid rgba(99,102,241,0.25)', borderRadius:8, padding:'6px 12px', margin:'0 12px 4px', fontSize:12, color:'var(--text-secondary)' },
  heroVideoChipClose: { background:'transparent', border:'none', color:'var(--text-muted)', fontSize:12, cursor:'pointer', padding:'0 2px', lineHeight:1 },
  input: {
    flex:1, padding:'9px 12px',
    background:'var(--bg-input)',
    border:'1px solid var(--border)',
    borderRadius:10,
    color:'var(--text-primary)',
    fontSize:13, outline:'none', minWidth:0,
    wordBreak:'break-word', overflowWrap:'anywhere',
    transition:'border-color 0.15s, box-shadow 0.15s',
    resize:'none', lineHeight:'1.5',
    minHeight:38, maxHeight:200, overflowY:'auto',
    fontFamily:'inherit',
  },
  sendBtn: {
    width:36, height:36, flexShrink:0,
    background:'linear-gradient(135deg,#4f46e5,#7c3aed)',
    color:'#fff', border:'none', borderRadius:10,
    fontWeight:700, cursor:'pointer', fontSize:16,
    display:'flex', alignItems:'center', justifyContent:'center',
    boxShadow:'0 2px 10px rgba(99,102,241,0.35)',
    transition:'opacity 0.15s, transform 0.1s, box-shadow 0.15s',
  },

  // ── Template banner ──────────────────────────────────────────────────────
  templateBanner: {
    display:'flex', flexDirection:'column', alignItems:'center',
    gap:10, padding:'20px 16px', textAlign:'center', width:'100%',
  },
  templateBannerIcon: { fontSize:30 },
  templateBannerTitle: { fontSize:14, fontWeight:700, color:'var(--text-primary)' },
  templateBannerDesc: { fontSize:12, color:'var(--text-muted)', lineHeight:1.65, maxWidth:260 },

  // ── Suggestion chips ─────────────────────────────────────────────────────
  suggestions: { display:'flex', flexWrap:'wrap', gap:6, justifyContent:'center', marginTop:4 },
  chip: {
    fontSize:11, fontWeight:600, color:'#a5b4fc',
    background:'rgba(99,102,241,0.07)',
    border:'1px solid rgba(99,102,241,0.18)',
    padding:'5px 11px', borderRadius:20,
    cursor:'pointer', fontFamily:'inherit',
    transition:'background 0.15s, border-color 0.15s, color 0.15s',
  },
};
